import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Search, Plus, Trash2, PackagePlus, Send, Zap, CalendarClock } from "lucide-react";

const REQ_STATUS = {
  pending: { label: "Onay Bekliyor", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Onaylandı · Hazırlanıyor", cls: "bg-sky-100 text-sky-800" },
  loaded: { label: "Araca Yüklendi", cls: "bg-emerald-100 text-emerald-800" },
  loaded_partial: { label: "Kısmi Yüklendi", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Reddedildi", cls: "bg-red-100 text-red-700" },
};

export default function VanStockRequest() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [urgency, setUrgency] = useState("normal");
  const [note, setNote] = useState("");
  const [prodQ, setProdQ] = useState("");
  const [results, setResults] = useState([]);
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  const loadMine = useCallback(() => {
    api.get("/van-sales/stock-requests").then((r) => setMine(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/van-sales/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
    loadMine();
  }, [loadMine]);

  useEffect(() => {
    if (!prodQ.trim()) { setResults([]); return; }
    const t = setTimeout(() => api.get("/van-sales/products", { params: { q: prodQ, limit: 20 } }).then((r) => setResults(r.data || [])).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [prodQ]);

  const add = (p) => {
    if (lines.find((l) => l.product_id === p.id)) { toast.info("Zaten listede"); return; }
    setLines((ls) => [...ls, { product_id: p.id, name: p.name, unit: p.unit, quantity: 1 }]);
  };
  const upd = (pid, q) => setLines((ls) => ls.map((l) => l.product_id === pid ? { ...l, quantity: q } : l));
  const rm = (pid) => setLines((ls) => ls.filter((l) => l.product_id !== pid));

  const submit = async () => {
    if (!vehicleId) { toast.error("Araç seçin"); return; }
    const items = lines.filter((l) => Number(l.quantity) > 0);
    if (!items.length) { toast.error("En az bir ürün ve adet girin"); return; }
    setBusy(true);
    try {
      await api.post("/van-sales/stock-requests", {
        vehicle_id: vehicleId, urgency, note: note || null,
        lines: items.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) })),
      });
      toast.success("Depo talebiniz amire/depoya iletildi");
      setLines([]); setNote(""); loadMine();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="van-stock-request">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><PackagePlus className="h-4 w-4 text-brand-green" /> Depodan Ürün Talep Et</div>
        <p className="mb-3 text-xs text-slate-500">Stok ekleyemez/çıkaramazsın — talep açarsın, depo/amirin onayladığını araca yükler.</p>
        <Label className="text-xs">Araç</Label>
        <select data-testid="stockreq-vehicle" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="">Araç seç</option>
          {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate}{v.driver_name ? ` · ${v.driver_name}` : ""}</option>)}
        </select>

        <div className="mt-3 flex gap-2">
          <button type="button" data-testid="stockreq-urgency-normal" onClick={() => setUrgency("normal")} className={`flex flex-1 items-center justify-center gap-1 rounded-md border px-3 py-2 text-xs font-medium ${urgency === "normal" ? "border-brand-green bg-emerald-50 text-brand-green" : "border-slate-200 text-slate-500"}`}><CalendarClock className="h-4 w-4" /> Ertesi Gün</button>
          <button type="button" data-testid="stockreq-urgency-urgent" onClick={() => setUrgency("urgent")} className={`flex flex-1 items-center justify-center gap-1 rounded-md border px-3 py-2 text-xs font-medium ${urgency === "urgent" ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-500"}`}><Zap className="h-4 w-4" /> Acil (Gün İçi)</button>
        </div>

        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="stockreq-search" value={prodQ} onChange={(e) => setProdQ(e.target.value)} placeholder="Talep edilecek ürün ara…" className="pl-9" />
        </div>
        {results.length > 0 && (
          <div className="mt-2 max-h-40 overflow-auto rounded-md border border-slate-100">
            {results.map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-slate-50 px-3 py-2 text-sm last:border-0">
                <span className="truncate">{p.name}</span>
                <Button size="sm" variant="outline" data-testid={`stockreq-add-${p.id}`} onClick={() => add(p)}><Plus className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-3 space-y-2" data-testid="stockreq-lines">
          {lines.length === 0 && <p className="py-3 text-center text-sm text-slate-400">Talep listesi boş.</p>}
          {lines.map((l) => (
            <div key={l.product_id} className="flex items-center gap-2" data-testid={`stockreq-row-${l.product_id}`}>
              <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{l.name}</span>
              <Input type="number" min="0" step="1" value={l.quantity} onChange={(e) => upd(l.product_id, e.target.value)} className="h-8 w-24" data-testid={`stockreq-qty-${l.product_id}`} />
              <span className="text-xs text-slate-400">{l.unit}</span>
              <button onClick={() => rm(l.product_id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not (opsiyonel)" className="mt-3 h-14 text-sm" data-testid="stockreq-note" />
        <Button className="mt-3 w-full" onClick={submit} disabled={busy} data-testid="stockreq-submit">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />} Talebi Gönder
        </Button>
      </div>

      <div className="space-y-3" data-testid="stockreq-mine">
        <div className="text-sm font-semibold text-slate-800">Taleplerim</div>
        {mine.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">Henüz talebiniz yok.</div>}
        {mine.map((r) => {
          const s = REQ_STATUS[r.status] || REQ_STATUS.pending;
          return (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`stockreq-item-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-800">{r.vehicle_plate} · {(r.lines || []).length} kalem {r.urgency === "urgent" && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-700">ACİL</span>}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.cls}`}>{s.label}</span>
              </div>
              <div className="mt-1 space-y-0.5 text-xs text-slate-500">
                {(r.lines || []).map((l, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{l.name}</span>
                    <span>{l.loaded_qty > 0 ? `yüklendi ${l.loaded_qty}` : l.approved_qty != null ? `onaylı ${l.approved_qty}` : `istenen ${l.quantity}`} {l.unit}</span>
                  </div>
                ))}
              </div>
              {(r.shortages || []).length > 0 && <div className="mt-1 text-[11px] text-red-600">{r.shortages.length} kalem stok yetersiz</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
